import React from 'react';
import { Typography, Box, styled } from '@mui/material';
import { numberToCurrencyString } from 'further-ui/utils';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { AllocationRule } from 'further-types/lp/allocations';
import { sumBy } from 'lodash';
import EqualisationsAllocationsTable, {
  ColumnConfig,
} from 'components/EqualisationElements/EqualisationsAllocationsTable';

const TableWrapper = styled(Box)(({ theme }) => ({
  '& tbody tr': {
    '& td': {
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
    },
  },
}));

interface RealisationAllocation {
  holdingClassId: string;
  commitmentId: string;
  investmentId: string;
  investorLabel: string;
  allocationPercentage: number;
  originalAllocationPercentage: number;
  allocationType: AllocationRule;
  receivedConsiderationLcy: number;
  earnoutConsiderationLcy: number;
  escrowConsiderationLcy: number;
  withholdingTaxPayableLcy: number;
  withholdingTaxExempt: boolean;
  unitsAllocated: number;
  excludeFromAllocation?: boolean;
}

type RealisationAllocationRow = RealisationAllocation & { key: number };

type RealisationAllocationColumnConfig = ColumnConfig & {
  renderCell?: (
    row: RealisationAllocationRow,
    rowIndex: number,
  ) => React.ReactNode;
};

interface RealisationAllocationsTableProps {
  realisationAllocations: Array<RealisationAllocation>;
  baseCurrency: CurrencyCode;
  allocationRule: AllocationRule;
}

const RealisationAllocationsTable: React.FC<
  RealisationAllocationsTableProps
> = ({ realisationAllocations, baseCurrency, allocationRule }) => {
  if (!realisationAllocations) {
    return <Typography variant="body2">No realisation allocations</Typography>;
  }

  const columns: Array<RealisationAllocationColumnConfig> = [
    {
      header: 'Investor',
      width: '15%',
      renderCell: (row) => row.investorLabel,
      renderFooter: () =>
        `${
          realisationAllocations.filter((a) => !a.excludeFromAllocation).length
        } investors`,
    },
    {
      header: 'Received consideration',
      width: '14%',
      renderCell: (row) =>
        numberToCurrencyString(row.receivedConsiderationLcy, {
          currency: baseCurrency,
        }),
      renderFooter: () =>
        numberToCurrencyString(
          sumBy(
            realisationAllocations.filter((a) => !a.excludeFromAllocation),
            'receivedConsiderationLcy',
          ),
          { currency: baseCurrency },
        ),
    },
    {
      header: 'Earnout consideration',
      width: '14%',
      renderCell: (row) =>
        numberToCurrencyString(row.earnoutConsiderationLcy, {
          currency: baseCurrency,
        }),
      renderFooter: () =>
        numberToCurrencyString(
          sumBy(
            realisationAllocations.filter((a) => !a.excludeFromAllocation),
            'earnoutConsiderationLcy',
          ),
          { currency: baseCurrency },
        ),
    },
    {
      header: 'Escrow consideration',
      width: '14%',
      renderCell: (row) =>
        numberToCurrencyString(row.escrowConsiderationLcy, {
          currency: baseCurrency,
        }),
      renderFooter: () =>
        numberToCurrencyString(
          sumBy(
            realisationAllocations.filter((a) => !a.excludeFromAllocation),
            'escrowConsiderationLcy',
          ),
          { currency: baseCurrency },
        ),
    },
    {
      header: 'Withholding tax',
      width: '12%',
      renderCell: (row) =>
        numberToCurrencyString(row.withholdingTaxPayableLcy, {
          currency: baseCurrency,
        }),
      renderFooter: () =>
        numberToCurrencyString(
          sumBy(
            realisationAllocations.filter((a) => !a.excludeFromAllocation),
            'withholdingTaxPayableLcy',
          ),
          { currency: baseCurrency },
        ),
    },
    {
      header: 'Units allocated',
      width: '12%',
      renderCell: (row) => row.unitsAllocated.toFixed(2),
      renderFooter: () =>
        sumBy(
          realisationAllocations.filter((a) => !a.excludeFromAllocation),
          'unitsAllocated',
        ).toFixed(2),
    },
    {
      header: 'Allocation (%)',
      width: '12%',
      renderCell: (row) => `${row.allocationPercentage.toFixed(2)}%`,
      renderFooter: () =>
        `${sumBy(
          realisationAllocations.filter((a) => !a.excludeFromAllocation),
          'allocationPercentage',
        ).toFixed(2)}%`,
    },
  ];

  const rows: Array<RealisationAllocationRow> = realisationAllocations.map(
    (allocation, idx) => ({
      key: idx,
      ...allocation,
    }),
  );

  const allocationRuleOptions = [
    { value: AllocationRule.Manual, label: 'Manual' },
    {
      value: AllocationRule.ByCommitmentAndClosingDate,
      label: 'By Commitment and Closing Date',
    },
    { value: AllocationRule.LinkedAction, label: 'Linked Action' },
    {
      value: AllocationRule.PerOriginalInvestment,
      label: 'Per Original Investment',
    },
  ];

  return (
    <TableWrapper>
      <EqualisationsAllocationsTable
        columns={columns}
        rows={rows}
        showAllocationRule={true}
        allocationRule={allocationRule}
        allocationRuleOptions={allocationRuleOptions}
        isNotEditable={true}
      />
    </TableWrapper>
  );
};

export default RealisationAllocationsTable;
